import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { buildApiUrl } from '../config/api';
import { migrateLocalStorageSettings } from '../utils/migrateLocalStorageSettings';
import { HAS_LOGGED_IN_KEY } from '../utils/storageKeys';

export interface User {
  id: number;
  nombre: string;
  apellidos: string;
  email: string;
  permisos: string;
  email_verified?: boolean;
  telefono?: string | null;
  url_imagen?: string | null;
  cif_dni?: string | null;
  num_cuenta?: string | null;
  vat_number?: string | null;
  direccion?: string | null;
  poblacion?: string | null;
  codigo_postal?: string | null;
  provincia?: string | null;
  pais?: string | null;
  plan?: 'starter' | 'pro';
  is_legacy_pro?: boolean;
  empresa?: string | null;
  nombre_empresa?: string | null;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  emailVerified: boolean; // Add email verification helper
  login: (token: string, user: User) => Promise<void>;
  logout: () => void;
  updateUser: (updatedUserData: Partial<User>) => void; // Add updateUser to AuthContextType
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Helper to get email verification status
  const emailVerified = user?.email_verified ?? false;

  const login = async (newToken: string, userData: User) => {
    setToken(newToken);
    setIsAuthenticated(true);
    setIsLoading(false);
    localStorage.setItem('token', newToken);
    if (!(userData as { isDemo?: boolean })?.isDemo) {
      try { localStorage.setItem(HAS_LOGGED_IN_KEY, '1'); } catch { /* sin storage */ }
    }
    
    // Fetch complete user data after login
    try {
      const response = await fetch(buildApiUrl(`/api/users/${userData.id}`), {
        headers: {
          'Authorization': `Bearer ${newToken}`
        }
      });
      
      if (response.ok) {
        const fullUserData = await response.json();
        const completeUser = fullUserData.user || fullUserData.data || userData;
        setUser(completeUser);
        localStorage.setItem('user', JSON.stringify(completeUser));
      } else {
        // If fetching full data fails, use the basic data
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (error) {
      console.error('Error fetching complete user data:', error);
      // Fallback to basic user data
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    }
    
    // Migrate localStorage settings to database
    try {
      await migrateLocalStorageSettings(newToken);
    } catch (error) {
      console.error('Error during settings migration:', error);
      // Don't fail the login process due to migration error
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsLoading(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const updateUser = (updatedUserData: Partial<User>) => {
    setUser((prevUser) => {
      if (prevUser) {
        const newUser = { ...prevUser, ...updatedUserData };
        localStorage.setItem('user', JSON.stringify(newUser));
        return newUser;
      }
      return null;
    });
  };

  const refreshUserData = async () => {
    if (!token || !user) return;
    
    try {
      const response = await fetch(buildApiUrl(`/api/users/${user.id}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const fullUserData = await response.json();
        const completeUser = fullUserData.user || fullUserData.data;
        setUser(completeUser);
        localStorage.setItem('user', JSON.stringify(completeUser));
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  useEffect(() => {
    const loadSession = async () => {
      // Detect ?token= param (admin "Login As" impersonation)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');

      if (urlToken) {
        // Clean URL immediately
        const cleanUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', cleanUrl);

        try {
          const response = await fetch(buildApiUrl('/api/auth/verify'), {
            headers: { 'Authorization': `Bearer ${urlToken}` }
          });

          if (response.ok) {
            const data = await response.json();
            setToken(urlToken);
            setUser(data.user);
            setIsAuthenticated(true);
            localStorage.setItem('token', urlToken);
            localStorage.setItem('user', JSON.stringify(data.user));
          } else {
            logout();
          }
        } catch {
          logout();
        }
        setIsLoading(false);
        return;
      }

      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // First set authenticated state with stored data immediately
          const parsedUser = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);
          setIsAuthenticated(true);
          setIsLoading(false);

          // Then validate token in background - set timeout for slower networks
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

          const response = await fetch(buildApiUrl('/api/auth/verify'), {
            headers: {
              'Authorization': `Bearer ${storedToken}`
            },
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            // Token is valid, get fresh user data from the response
            const data = await response.json();
            setUser(data.user);
            // Update localStorage with fresh user data
            localStorage.setItem('user', JSON.stringify(data.user));
          } else if (response.status === 401 || response.status === 403) {
            // Token is invalid or expired
            logout();
          }
          // For other API errors (500, network issues), keep using stored auth
        } catch (error: any) {
          // Only logout for authentication errors, not network issues
          if (error.name !== 'AbortError') {
            console.warn('Token verification failed, using stored auth:', error);
            // Keep the user authenticated with stored data
          }
        }
      } else {
        // No token or user in local storage, ensure logged out state
        setIsLoading(false);
      }
    };

    loadSession();
  }, []); // Empty dependency array means this effect runs once on mount

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isLoading, emailVerified, login, logout, updateUser, refreshUserData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
